import { useState, useCallback, useRef, useEffect } from 'react';
import type { LayoutItem } from 'react-grid-layout';

/** Responsive breakpoints matching react-grid-layout conventions */
export const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480 } as const;
export const COLS = { lg: 12, md: 10, sm: 6, xs: 4 } as const;
export const ROW_HEIGHT = 80;

interface UseGridLayoutOptions {
  initialLayout: LayoutItem[];
  onSave: (positions: Array<{ id: number; x: number; y: number; w: number; h: number }>) => void;
  autoSaveDelay?: number;
}

/**
 * Generate responsive layouts for all breakpoints from a base (lg) layout.
 * - md: clamp width to 10 cols, re-wrap items that overflow
 * - sm: single column, full-width stacking (6 cols, each widget w=6)
 * - xs: single column, full-width stacking (4 cols, each widget w=4)
 */
export function generateResponsiveLayouts(lgLayout: LayoutItem[]): Record<string, LayoutItem[]> {
  const sorted = [...lgLayout].sort((a, b) => a.y - b.y || a.x - b.x);

  // md: fit within 10 cols
  const md: LayoutItem[] = sorted.map((item) => ({
    ...item,
    w: Math.min(item.w, COLS.md),
    x: Math.min(item.x, COLS.md - Math.min(item.w, COLS.md)),
  }));

  // sm: stack in single column, full-width
  let smY = 0;
  const sm: LayoutItem[] = sorted.map((item) => {
    const mapped = { ...item, x: 0, w: COLS.sm, y: smY, h: Math.max(item.h, 3) };
    smY += mapped.h;
    return mapped;
  });

  // xs: stack in single column, full-width
  let xsY = 0;
  const xs: LayoutItem[] = sorted.map((item) => {
    const mapped = { ...item, x: 0, w: COLS.xs, y: xsY, h: Math.max(item.h, 3) };
    xsY += mapped.h;
    return mapped;
  });

  return { lg: lgLayout, md, sm, xs };
}

/**
 * Manages react-grid-layout state with dirty tracking and debounced auto-save.
 */
export function useGridLayout({
  initialLayout,
  onSave,
  autoSaveDelay = 1000,
}: UseGridLayoutOptions) {
  const [layout, setLayout] = useState<LayoutItem[]>(initialLayout);
  const [isDirty, setIsDirty] = useState(false);
  const [currentBreakpoint, setCurrentBreakpoint] = useState<string>('lg');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync with external layout changes (e.g. after refetch)
  useEffect(() => {
    setLayout(initialLayout);
    setIsDirty(false);
  }, [initialLayout]);

  const handleLayoutChange = useCallback(
    (newLayout: LayoutItem[]) => {
      setLayout(newLayout);
      setIsDirty(true);

      // Debounced auto-save
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        const positions = newLayout.map((item) => ({
          id: parseInt(item.i, 10),
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
        }));
        onSave(positions);
        setIsDirty(false);
      }, autoSaveDelay);
    },
    [onSave, autoSaveDelay],
  );

  const handleBreakpointChange = useCallback((bp: string) => {
    setCurrentBreakpoint(bp);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  return {
    layout,
    isDirty,
    currentBreakpoint,
    handleLayoutChange,
    handleBreakpointChange,
    BREAKPOINTS,
    COLS,
    ROW_HEIGHT,
  };
}
